import {
  SkyBadge,
  SkyButton,
  SkyDrawer,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
  SkyTooltip,
} from "@skypointcloud/skypoint-ui";
import { type ReactElement, type ReactNode, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import type { TransformationGraph, DbtJobRunRow } from "@shared/types/transformations";
import { ErrorState } from "@/components/access/ScreenStates";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { CopyIcon, ExternalLinkIcon } from "@/lib/icons";
import { ROUTES, absolutePath } from "@/lib/routes";
import {
  BUNDLE_DETAIL_WITHHELD,
  DBT_SETTINGS_PATH,
  DBT_SETTINGS_PLACE,
  EDIT_DBT_SETTINGS_LABEL,
  VIEW_DBT_SETTINGS_LABEL,
  ASK_ADMIN_DESTINATION,
  WAREHOUSE_SETTINGS_PLACE,
} from "@/lib/transformations/dbtSettings";
import { GRAPH_UNREADABLE_SENTENCE } from "@/lib/transformations/landingStatus";
import {
  gitRefTerm,
  repoCommitUrl,
  repoHostName,
  repoShortName,
} from "@/lib/transformations/repoFileUrl";
import { modelRows } from "@/lib/transformations/modelGroups";
import { runOutcome } from "@/lib/transformations/runOutcome";
import { modelsBuiltSentence, runAndStepDuration } from "@/lib/transformations/runFacts";
import { testsLine } from "./TransformationHistoryView";
import { NOT_STARTED_LABEL } from "@/components/runs/runFormatting";
import { ProjectFrom } from "./TransformationModelsView";
import { BreakableAddress, RepositoryWithheld } from "./RepositoryName";
import { runNotStarted, runStartOf } from "./transformFormatting";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * "Details" on the Transformations landing: the dbt project's facts in a side panel (Session 11,
 * P5; ux-s11-transformations.md §2.3, ledger P1-P4). Everything the removed success banner and
 * "Last transform step" card said is here, one press away instead of on every visit: the
 * repository, branch, project folder and the last run's commit; the dbt version (#1207: no image tag); where
 * dbt builds (names only); the last run with its outcome and counts; the project's counts and the
 * run they are from; and, in the Repository section (round 6, T2), the way to where the repository
 * is set: "Edit dbt repository settings" for `dbt_jobs.write`, "View dbt repository
 * settings" otherwise. Round 6 (W12): a field the binding does not set reads "Not set", never its
 * default as if configured, and says where it is set.
 *
 * Read-only for every role; the settings button only for `instance.binding.read`. Modal: focus
 * goes into the panel (the library traps it there) and the landing hands it back to "Details" on
 * close; Escape closes it.
 */

export interface TransformationDetailsDrawerProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  graph: TransformationGraph | undefined;
  graphState: "loading" | "ready" | "error";
  graphError: unknown;
  onRetry(): void;
  /** The newest transform step (`useDbtJobRuns(1)`), null when there is none. */
  newest: DbtJobRunRow | null;
  /**
   * The runs read's state. `newest` is null both when no run exists and when the read has not
   * answered or failed; only "ready" lets the panel say "No transformation run yet."
   */
  runsState: "loading" | "ready" | "error";
  runsError: unknown;
  onRetryRuns(): void;
  canOpenDestination: boolean;
  /**
   * Round 6 (T2): `dbt_jobs.write`, which edits the dbt repository tab. The settings
   * button reads "Edit dbt repository settings" with it and "View dbt repository settings"
   * without; it is rendered only with `canOpenDestination`.
   */
  canEditDbtSettings?: boolean;
}

/**
 * One fact: the term, the value, and (round 6) an optional muted note on its own line under the
 * value, as a second `dd` of the same term, so the value's own text stays what it says.
 */
function Fact({
  term,
  children,
  testId,
  note,
}: {
  term: string;
  children: ReactNode;
  testId: string;
  note?: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1 py-1">
      <dt>
        <SkyText size={200} color="muted">
          {term}
        </SkyText>
      </dt>
      <dd className="min-w-0 break-words" data-testid={testId}>
        {children}
      </dd>
      {note ? (
        <dd className="col-start-2 min-w-0" data-testid={`${testId}-note`}>
          <SkyText size={200} color="muted" block>
            {note}
          </SkyText>
        </dd>
      ) : null}
    </div>
  );
}

function Section({
  title,
  children,
  footer,
}: {
  title: string;
  children: ReactNode;
  /** Round 6 (T2): what follows the facts, inside the section (the settings button). */
  footer?: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1" aria-label={title}>
      <SkyText size={300} weight="semibold">
        {title}
      </SkyText>
      <dl className="border-t border-border pt-1">{children}</dl>
      {footer}
    </section>
  );
}

/** Round 6 (W12): a field the binding does not set, never its default shown as if it were. */
const NOT_SET = "Not set";

function Mono({ children }: { children: ReactNode }) {
  return (
    <SkyText size={200} font="monospace">
      {children}
    </SkyText>
  );
}

function Plain({ children, title }: { children: ReactNode; title?: string | undefined }) {
  return (
    <SkyText size={200} title={title}>
      {children}
    </SkyText>
  );
}

const DRAWER_TEST_ID = "transformation-details-drawer";

const DESTINATION_WORDS: Record<string, string> = {
  snowflake: "Snowflake",
  databricks: "Databricks",
};

/**
 * Round 6 (T2, owner): the address ran past the pane and wrapped at its hyphen. The link reads
 * "owner/repo" then a muted "on GitHub", one line that truncates; the whole address is in a
 * tooltip and in the link's accessible name, and it is still the link to the repository.
 */
function Repository({ bundle }: { bundle: TransformationGraph["bundle"] }) {
  // #1306: set, but the address is Instance admin and above.
  if (bundle.repoBound && bundle.repoUrlWithheld) {
    return <RepositoryWithheld testId="transformation-details-repository-withheld" />;
  }
  if (!bundle.repoBound || !bundle.repoUrl) return <Plain>{NOT_SET}</Plain>;
  const host = repoHostName(bundle.repoUrl);
  const short = repoShortName(bundle.repoUrl);
  if (!host || !short) {
    return <BreakableAddress repoUrl={bundle.repoUrl} size={200} />;
  }
  return (
    <SkyTooltip content={bundle.repoUrl}>
      <a
        href={bundle.repoUrl}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex min-w-0 max-w-full items-center gap-1 underline-offset-2 hover:underline"
        aria-label={`${bundle.repoUrl}, opens the repository in ${host} in a new tab`}
        data-testid="transformation-details-repository-link"
      >
        <SkyText size={200} font="monospace" color="accent" truncate block className="min-w-0">
          {short}
        </SkyText>{" "}
        <SkyText size={200} color="muted" className="shrink-0 whitespace-nowrap">
          {`on ${host}`}
        </SkyText>
        <ExternalLinkIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      </a>
    </SkyTooltip>
  );
}

/** The note under the repository: why a private one can look missing, or where it is set. */
function repositoryNote(bundle: TransformationGraph["bundle"]): string | undefined {
  // #1306: the value already says who to ask; no note about a host the reader cannot see.
  if (bundle.repoBound && bundle.repoUrlWithheld) return undefined;
  if (!bundle.repoBound || !bundle.repoUrl) return `Set under ${DBT_SETTINGS_PLACE}.`;
  const host = repoHostName(bundle.repoUrl);
  // skyData cannot know whether a repository is private without asking the host, so this is
  // said for every known host, as a condition, never as a detected fact.
  return host
    ? `If this repository is private, ${host} shows Page not found unless you are signed in to ${host} with access to it. skyData clones it with the token saved on the dbt repository tab.`
    : "skyData clones it with the token saved on the dbt repository tab.";
}

/**
 * Round 6 (T2): the commit as its 7-character short sha, linked to the commit on the host when
 * the host is known, the full sha on hover and in the name, and a Copy button for the whole sha.
 */
function Commit({ commit, repoUrl }: { commit: string; repoUrl: string | null }) {
  const notice = useNotice();
  const short = commit.slice(0, 7);
  const url = repoCommitUrl(repoUrl, commit);
  const host = repoHostName(repoUrl);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(commit);
      notice.info("Commit copied", { forPath: ROUTES.transformations });
    } catch {
      notice.info(`Commit ${commit}: copy it from the screen.`, {
        forPath: ROUTES.transformations,
      });
    }
  };
  return (
    <span className="flex min-w-0 items-center gap-1">
      {url && host ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          title={commit}
          className="underline-offset-2 hover:underline"
          aria-label={`Commit ${commit}, opens it in ${host} in a new tab`}
        >
          <SkyText size={200} font="monospace" color="accent">
            {short}
          </SkyText>
        </a>
      ) : (
        <SkyText size={200} font="monospace" title={commit}>
          {short}
        </SkyText>
      )}
      <SkyButton
        appearance="subtle"
        size="small"
        icon={CopyIcon}
        aria-label="Copy the commit"
        className="shrink-0"
        testId="transformation-details-commit-copy"
        onClick={() => void copy()}
      />
    </span>
  );
}

/**
 * Round 6 (T4): the schemas the last manifest's models, seeds and snapshots build into, so the
 * pane does not keep saying the binding's schema once the project builds elsewhere. Sources are
 * read, not built, so they are not listed. Null when the manifest names none.
 */
function buildsInto(graph: TransformationGraph | undefined): string[] | null {
  if (!graph?.project) return null;
  const schemas = new Set<string>();
  for (const node of graph.nodes) {
    if (node.resourceType === "source") continue;
    if (node.schema) schemas.add(node.schema);
  }
  return schemas.size > 0 ? [...schemas].sort((a, b) => a.localeCompare(b)) : null;
}

export const LAST_RUN_UNREADABLE_SENTENCE = "The last run could not be read.";

function LastRun({
  row,
  state,
  error,
  onRetry,
}: {
  row: DbtJobRunRow | null;
  state: "loading" | "ready" | "error";
  error: unknown;
  onRetry(): void;
}) {
  const zone = useDisplayTz();
  // A failed or pending runs read is not "no run": the status line says "the last run could not
  // be read" then, and the panel must not contradict it.
  if (!row && state === "error") {
    return (
      <Fact term="Last run" testId="transformation-details-last-run">
        <div data-testid="transformation-details-last-run-error">
          <ErrorState title={LAST_RUN_UNREADABLE_SENTENCE} error={error} onRetry={onRetry} />
        </div>
      </Fact>
    );
  }
  if (!row && state === "loading") {
    return (
      <Fact term="Last run" testId="transformation-details-last-run">
        <SkySkeleton testId="transformation-details-last-run-loading" aria-busy="true">
          <SkySkeletonItem size={16} />
        </SkySkeleton>
      </Fact>
    );
  }
  if (!row) {
    return (
      <Fact term="Last run" testId="transformation-details-last-run">
        <Plain>No transformation run yet.</Plain>
      </Fact>
    );
  }
  const outcome = runOutcome(row);
  // Round 6, T5: built is the models that succeeded; skipped ones are named, never counted built.
  const built = modelsBuiltSentence(row);
  return (
    <>
      <Fact term="Run" testId="transformation-details-last-run">
        <Link
          href={absolutePath(`/runs/${row.runId}`)}
          className="underline-offset-2 hover:underline"
        >
          <SkyText size={200} font="monospace" color="accent">
            {row.runId}
          </SkyText>
        </Link>
      </Fact>
      <Fact term="Outcome" testId="transformation-details-outcome">
        <SkyBadge appearance="tint" color={outcome.color}>
          {outcome.word}
        </SkyBadge>
      </Fact>
      <Fact term="Started" testId="transformation-details-started">
        <Plain title={runNotStarted(row) ? undefined : instantHoverTitle(runStartOf(row), zone)}>
          {runNotStarted(row) ? NOT_STARTED_LABEL : formatInstant(runStartOf(row), zone, "seconds")}
        </Plain>
      </Fact>
      <Fact term="Duration" testId="transformation-details-duration">
        {/* W6: "Run 1m 14s · dbt step 1m 04s", the two numbers Runs and History each show. */}
        <Plain>{runAndStepDuration(row)}</Plain>
      </Fact>
      <Fact term="Models" testId="transformation-details-models">
        <Plain>{built}</Plain>
      </Fact>
      <Fact term="Tests" testId="transformation-details-tests">
        <Plain>{testsLine(row)}</Plain>
      </Fact>
    </>
  );
}

/** The commit of the last FINISHED run: a step in flight has not checked out yet. */
function lastCommit(
  newest: DbtJobRunRow | null,
  inFlight: boolean,
  graph: TransformationGraph | undefined,
): string | null {
  return (newest && !inFlight ? newest.commit : graph?.run?.commit) ?? null;
}

type Bundle = NonNullable<TransformationGraph["bundle"]>;

/**
 * Round 6 (T2): named by what it opens, beside the facts it is about. "Open Destination" read as
 * the GitHub link's destination on this screen.
 */
function DbtSettingsLink({ canEdit, onOpen }: { canEdit: boolean; onOpen: () => void }) {
  return (
    // Round 6 (T2): named by what it opens, beside the facts it is about. "Open
    // Destination" read as the GitHub link's destination on this screen.
    <div className="flex flex-col items-start gap-2 pt-2">
      <SkyText size={200} color="muted" testId="transformation-details-settings-line">
        The repository, branch, project folder and access token are set on Destination, dbt
        repository tab.
      </SkyText>
      <SkyButton
        appearance="secondary"
        size="small"
        testId="transformation-details-open-destination"
        onClick={onOpen}
      >
        {canEdit ? EDIT_DBT_SETTINGS_LABEL : VIEW_DBT_SETTINGS_LABEL}
      </SkyButton>
    </div>
  );
}

/** The project folder: not set without a repository, the subfolder, or the repository root. */
function projectFolder(bundle: Bundle): ReactElement {
  if (!bundle.repoBound) return <Plain>{NOT_SET}</Plain>;
  if (bundle.detailsWithheld) return <Plain>{BUNDLE_DETAIL_WITHHELD}</Plain>;
  return bundle.subdir ? <Mono>{bundle.subdir}</Mono> : <Plain>repository root</Plain>;
}

function RepositorySection({
  bundle,
  commit,
  footer,
}: {
  bundle: Bundle;
  commit: string | null;
  footer: ReactNode;
}) {
  return (
    <Section title="Repository" footer={footer}>
      <Fact
        term="Repository"
        testId="transformation-details-repository"
        note={repositoryNote(bundle)}
      >
        <Repository bundle={bundle} />
      </Fact>
      {/* Round 7 T-38: Branch, not Ref (a 40-hex commit reads "Tag or commit"); the test
          id keeps its word. */}
      <Fact
        term={bundle.repoBound && bundle.ref ? gitRefTerm(bundle.ref) : "Branch"}
        testId="transformation-details-ref"
      >
        {!bundle.repoBound ? (
          <Plain>{NOT_SET}</Plain>
        ) : bundle.detailsWithheld || !bundle.ref ? (
          <Plain>{BUNDLE_DETAIL_WITHHELD}</Plain>
        ) : (
          <Mono>{bundle.ref}</Mono>
        )}
      </Fact>
      <Fact term="Project folder" testId="transformation-details-folder">
        {projectFolder(bundle)}
      </Fact>
      <Fact term="Commit" testId="transformation-details-commit">
        {commit ? <Commit commit={commit} repoUrl={bundle.repoUrl} /> : <Plain>not recorded</Plain>}
      </Fact>
    </Section>
  );
}

/**
 * #1207 with #1191: the dbt version a run used, which a dbt author needs. The image tag skyData
 * runs it in is an internal name and is not shown (the Outcome above says how the run ended).
 */
function DbtVersionSection({ graph }: { graph: TransformationGraph | undefined }) {
  return (
    <Section title="dbt">
      <Fact term="dbt version" testId="transformation-details-dbt-version">
        <Plain>{graph?.project?.dbtVersion ?? graph?.run?.dbtVersion ?? "not recorded"}</Plain>
      </Fact>
    </Section>
  );
}

function TargetSection({
  target,
  schemas,
  withheld,
}: {
  target: Bundle["target"] | null;
  schemas: string[] | null;
  withheld: boolean;
}) {
  return (
    <Section title="Target">
      {target && target.accountBound !== false ? (
        <>
          <Fact term="Destination" testId="transformation-details-target-type">
            <Plain>{DESTINATION_WORDS[target.destinationType] ?? target.destinationType}</Plain>
          </Fact>
          <Fact term="Database" testId="transformation-details-target-database">
            {target.database ? <Mono>{target.database}</Mono> : <Plain>{NOT_SET}</Plain>}
          </Fact>
          {/* T4: dbt builds here unless the project names another schema. */}
          <Fact term="Default schema" testId="transformation-details-target-schema">
            {target.schema ? <Mono>{target.schema}</Mono> : <Plain>{NOT_SET}</Plain>}
          </Fact>
          <Fact term="Builds into" testId="transformation-details-builds-into">
            {schemas ? (
              <Mono>{schemas.join(", ")}</Mono>
            ) : (
              <Plain>Not known until a run reads the project</Plain>
            )}
          </Fact>
          <Fact term="Warehouse" testId="transformation-details-target-warehouse">
            {withheld ? (
              // #1362: the warehouse is Instance admin and above; the database and schema are not.
              <Plain>{ASK_ADMIN_DESTINATION}</Plain>
            ) : target.warehouse ? (
              <Mono>{target.warehouse}</Mono>
            ) : (
              <Plain>{NOT_SET}</Plain>
            )}
          </Fact>
        </>
      ) : (
        // W12: no binding, or one with no warehouse account: its defaults are not a target.
        <Fact
          term="Destination"
          testId="transformation-details-target-type"
          note={`Set under ${WAREHOUSE_SETTINGS_PLACE}.`}
        >
          <Plain>{NOT_SET}</Plain>
        </Fact>
      )}
    </Section>
  );
}

function ProjectSection({ graph }: { graph: TransformationGraph }) {
  // The Models view's own rows (models, seeds, snapshots and dbt-disabled models), so this count
  // matches "Models {n}" on the view switch; the disabled ones are named in brackets.
  const models = modelRows(graph.nodes, graph.disabledNodes ?? []).length;
  const disabled = graph.disabledNodes?.length ?? 0;
  const sources = graph.nodes.filter((node) => node.resourceType === "source").length;
  return (
    <Section title="Project">
      <Fact term="Contents" testId="transformation-details-project">
        {graph.project ? (
          <Plain>
            {`${models} ${models === 1 ? "model" : "models"}${
              disabled > 0 ? ` (${disabled} disabled)` : ""
            } · ${graph.tests.length} ${
              graph.tests.length === 1 ? "test" : "tests"
            } · ${sources} ${sources === 1 ? "source" : "sources"}`}
          </Plain>
        ) : (
          <Plain>Not read yet: the project appears after a run reaches dbt.</Plain>
        )}
      </Fact>
      {graph.project ? (
        <Fact term="From" testId="transformation-details-project-from">
          <SkyText size={200}>
            <ProjectFrom graph={graph} testId="transformation-details-project-run" />
          </SkyText>
        </Fact>
      ) : null}
    </Section>
  );
}

export function TransformationDetailsDrawer({
  open,
  onOpenChange,
  graph,
  graphState,
  graphError,
  onRetry,
  newest,
  runsState,
  runsError,
  onRetryRuns,
  canOpenDestination,
  canEditDbtSettings = false,
}: TransformationDetailsDrawerProps) {
  const [, navigate] = useLocation();
  const body = useRef<HTMLDivElement>(null);
  // UX §2.3: focus starts on Close. The library focuses its first tabbable element, the resize
  // handle; this moves it once, on the next task, after the library's own open focus.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      body.current
        ?.closest('[role="dialog"]')
        ?.querySelector<HTMLButtonElement>(`[data-testid="${DRAWER_TEST_ID}-close"]`)
        ?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);
  const bundle = graph?.bundle;
  const inFlight = newest ? runOutcome(newest).inFlight : false;
  // The commit of the last FINISHED run: a step in flight has not checked out yet.
  const commit = lastCommit(newest, inFlight, graph);
  const schemas = buildsInto(graph);

  return (
    <SkyDrawer
      open={open}
      onOpenChange={onOpenChange}
      position="end"
      // Round 6 (T2): 600 px, the preset AuditEventDrawer and ConnectorDetailDrawer use.
      size="large"
      headerTitle="dbt project"
      subtitle="What the next transformation run clones and where it builds."
      testId={DRAWER_TEST_ID}
    >
      <div ref={body} className="flex flex-col gap-5">
        {graphState === "error" ? (
          <div data-testid="transformation-details-graph-error">
            <ErrorState title={GRAPH_UNREADABLE_SENTENCE} error={graphError} onRetry={onRetry} />
          </div>
        ) : null}

        {bundle ? (
          <RepositorySection
            bundle={bundle}
            commit={commit}
            footer={
              canOpenDestination ? (
                <DbtSettingsLink
                  canEdit={canEditDbtSettings}
                  onOpen={() => navigate(absolutePath(DBT_SETTINGS_PATH))}
                />
              ) : null
            }
          />
        ) : null}

        {bundle ? <DbtVersionSection graph={graph} /> : null}

        {bundle ? (
          <TargetSection
            target={bundle.target ?? null}
            schemas={schemas}
            withheld={bundle.detailsWithheld === true}
          />
        ) : null}

        <Section title="Last run">
          <LastRun row={newest} state={runsState} error={runsError} onRetry={onRetryRuns} />
        </Section>

        {graph ? <ProjectSection graph={graph} /> : null}
      </div>
    </SkyDrawer>
  );
}
